import { auth } from './firebase';

// Get the backend URL based on environment
const getBackendUrl = () => {
  return window.location.hostname === 'localhost'
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';
};

// Helper to get auth headers
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const user = auth.currentUser;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (user) {
    try {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }
  }
  
  return headers;
};

// Helper for authenticated fetch
export const authenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = `${getBackendUrl()}${endpoint}`;
  const headers = await getAuthHeaders();
  
  // Merge headers
  const mergedHeaders = {
    ...headers,
    ...(options.headers || {}),
  };
  
  return fetch(url, {
    ...options,
    headers: mergedHeaders,
  });
};

// API service methods
export const apiService = {
  // Performance logging
  async logPerformance(data: any) {
    return authenticatedFetch('/api/log-performance', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get performance stats (admin only)
  async getPerformanceStats(days: number = 7) {
    const response = await authenticatedFetch(`/api/performance-stats?days=${days}`);
    if (!response.ok) throw new Error('Failed to fetch performance stats');
    return response.json();
  },

  // Get recent logs (admin only)
  async getRecentLogs(limit: number = 50) {
    const response = await authenticatedFetch(`/api/recent-logs?limit=${limit}`);
    if (!response.ok) throw new Error('Failed to fetch recent logs');
    return response.json();
  },

  // Sentences API
  async getSentences(params: Record<string, string> = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/sentences?${queryString}` : '/api/sentences';
    const response = await authenticatedFetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch sentences');
    return response.json();
  },

  async approveSentence(sentenceId: string, adminId: string) {
    return authenticatedFetch(`/api/sentences/${sentenceId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ admin_id: adminId }),
    });
  },

  async rejectSentence(sentenceId: string, adminId: string, reason: string) {
    return authenticatedFetch(`/api/sentences/${sentenceId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ admin_id: adminId, rejection_reason: reason }),
    });
  },

  // Phonemes API
  async getPendingPhonemes() {
    const response = await authenticatedFetch('/api/phonemes/pending');
    if (!response.ok) throw new Error('Failed to fetch pending phonemes');
    return response.json();
  },

  async approvePhoneme(phonemeId: string) {
    return authenticatedFetch(`/api/phonemes/${phonemeId}/approve`, {
      method: 'POST',
    });
  },

  async rejectPhoneme(phonemeId: string, reason: string) {
    return authenticatedFetch(`/api/phonemes/${phonemeId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  async uploadPhoneme(formData: FormData) {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const token = await user.getIdToken();
    const response = await fetch(`${getBackendUrl()}/api/phonemes/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // Don't set Content-Type for FormData
    });
    
    if (!response.ok) throw new Error('Failed to upload phoneme');
    return response.json();
  },

  // User profile
  async getUserProfile() {
    const response = await authenticatedFetch('/api/user/profile');
    if (!response.ok) {
      // Profile endpoint might not exist yet
      return null;
    }
    return response.json();
  },

  // Clean up logs (admin only)
  async cleanupLogs() {
    return authenticatedFetch('/api/cleanup-logs', {
      method: 'POST',
    });
  },
};

export default apiService;